import { useMachine } from "@xstate/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RadioGroup from "./RadioGroup";
import styled from "styled-components";
import handleDelayScroll from "@utils/handleDelayScroll";
import CheckboxGroup from "./CheckBoxGroup";
import { flatMap } from "lodash";
import useGlobalErrorHandler from "@hooks/useGlobalErrorHandler";
import { SHORT_FORM_CONFIG } from "@constants/antibiotics";
import { debugUISync } from "@utils/debugUtil";
import { smartSortOptions } from '@constants/ageRange';

const Wrapper = styled.div`
  margin-bottom: 100px;
`;

const ErrorMessage = styled.div`
  color: red;
  padding: 10px;
  margin-bottom: 15px;
  background-color: #ffeeee;
  border-radius: 4px;
`;

type Option = { value: string; label: string };

interface Question {
  question: string;
  options: Option[];
  selectedValues: string | string[];
  isMultiSelect: boolean;
  flow: string;
  checklistName?: string; // Track which checklist this multiselect uses
}

interface Props {
  stateMachine: any;
  onFormFinished?: (context?: any, events?: string[]) => void;
  preFiringEvents?: { type: string; param?: { [key: string]: string } }[];
  onReset?: () => void;
  onFormStart?: () => void;
}

/* TODO:
- Not showing selected options if you edit after completing flow
- Not resetting selected bodysystem after edit
*/

const StatelyForm = ({
  stateMachine,
  onFormFinished,
  preFiringEvents,
  onReset,
  onFormStart,
}: Props) => {
  const [snapshot, send] = useMachine(stateMachine);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sendTo = send as any;

  const currentQuestionRef = useRef<HTMLDivElement>(null);
  const context = snapshot.context;
  const [isFormFinished, setIsFormFinished] = useState(!!context.link);
  const [multiSelectedValues, setMultiSelectedValues] = useState<string[]>([]);
  const [isMultiSelectDisabled, setIsMultiSelectDisabled] =
    useState<Map<number, boolean>>();
  const [questions, setQuestions] = useState<Question[]>([]);

  useGlobalErrorHandler(setErrorMessage);

  useEffect(() => {
    onFormStart?.();
  }, [onFormStart]);

  useEffect(() => {
    setIsFormFinished(!!context.link);
  }, [context.link, context.reset]);

  useEffect(() => {
    // reset state machine when component gets unmounted
    return () => {
      send({ type: "resetContext" });
      onReset?.();
    };
  }, [send, onReset]);

  useEffect(() => {
    if (isFormFinished) {
      const flattendEvents = flatMap(
        questions.map((question) => question.selectedValues)
      );
      onFormFinished?.(context, flattendEvents);
    }
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [isFormFinished, context, onFormFinished]);

  const runPreFiringEvents = useCallback(() => {
    if (preFiringEvents && preFiringEvents?.length > 0) {
      preFiringEvents.forEach((event) => {
        sendTo({ type: event.type, ...event.param });
      });
    }
    setErrorMessage(null);
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [sendTo]);

  useEffect(() => {
    runPreFiringEvents();
  }, [runPreFiringEvents]);

  const checkForShortform = useCallback((item: string) => {
    if (!item || typeof item !== 'string') {
      return item || '';
    }
    const upperItem = item.toUpperCase();
    const result = SHORT_FORM_CONFIG[upperItem] || item;

    return result;
  }, []);

const getOptions = useCallback(
    (currentValue: string, currentFlow: string) => {
      const flow =
        currentFlow && currentFlow === context.subflow?.id
          ? context.subflow
          : stateMachine;
      const events = flow.states[currentValue]?.on ?? {};
      const options = Object.keys(events).map((key) => {
        const entry = events?.[key]?.[0];
        const option = checkForShortform(entry?.description || key);
        return { label: option, value: key };
      });

      // Get the current question text from the state description
      const currentQuestion = flow.states[currentValue]?.description || '';
      
      // Use smart sorting - maps our {label, value} structure to what smartSortOptions expects
      const optionsForSorting = options.map(opt => ({ value: opt.label }));
      const sortedOptions = smartSortOptions(currentQuestion, optionsForSorting);
      
      // Map back to original structure while preserving the smart sort order
      return sortedOptions.map(sortedOpt => {
        const originalOption = options.find(opt => opt.label === sortedOpt.value);
        return originalOption;
      }).filter(Boolean); // Remove any undefined entries
    },
    [context.subflow, stateMachine, checkForShortform]
  );

  const getTitle = useCallback(
    (currentValue: string, currentFlow: string) => {
      const flow =
        currentFlow && currentFlow === context.subflow?.id
          ? context.subflow
          : stateMachine;
      const rawTitle = flow.states[currentValue]?.description || currentValue;
      return checkForShortform(rawTitle);
    },
    [context.subflow, stateMachine, checkForShortform]
  );

const getDescription = useCallback(
  (currentValue: string, currentFlow: string) => {
    const flow =
      currentFlow && currentFlow === context.subflow?.id
        ? context.subflow
        : stateMachine;
    const rawDescription = flow.states[currentValue]?.tags || "";

    // Handle both string and array formats
    const descriptionString = Array.isArray(rawDescription)
      ? rawDescription[0] || ""
      : rawDescription;

    return checkForShortform(descriptionString);
  },
  [context.subflow, stateMachine, checkForShortform]
);

  const checklist: { item: string; label: string; selected: boolean }[] = useMemo(() => {
    const checkName = !context.isSubflow
      ? context.checkName
      : context.snapshot?.context.checkName;
    const checklist = !context.isSubflow
      ? context.checklist?.[checkName]
      : context.snapshot?.context.checklist?.[checkName];
    
    debugUISync('--- REACT: Computing checklist ---', 
      `checkName: ${checkName}, isSubflow: ${context.isSubflow}, context.checklist: ${JSON.stringify(context.checklist)}, snapshot.context.checklist: ${JSON.stringify(context.snapshot?.context.checklist)}, result: ${JSON.stringify(checklist)}`);
    
    return checklist ?? [];
  }, [context]);

  // Get the target checklist name for the current multiselect being edited
  const targetChecklistName = useMemo(() => {
    // During editing, determine which multiselect we're actually targeting
    // by looking at which question in the questions array we're editing
    const currentlyEditingIndex = questions.findIndex(q => !q.isMultiSelect || 
      (q.isMultiSelect && isMultiSelectDisabled?.get(questions.indexOf(q)) === false));
    
    if (currentlyEditingIndex >= 0) {
      const editingQuestion = questions[currentlyEditingIndex];
      if (editingQuestion?.isMultiSelect && editingQuestion?.checklistName) {
        return editingQuestion.checklistName;
      }
    }
    
    // Fallback to current context
    return !context.isSubflow ? context.checkName : context.snapshot?.context.checkName;
  }, [questions, isMultiSelectDisabled, context.isSubflow, context.checkName, context.snapshot?.context.checkName]);

  const options = useMemo(() => {
    return checklist?.map((factor: { item: string; label: string }) => ({
      value: factor.item,
      label: checkForShortform(factor.label),
    }))?.sort((a, b) => a.label.localeCompare(b.label));
  }, [checkForShortform, checklist]);

  // Sync multiSelectedValues with actual checklist state from state machine
  // Use the target checklist name to get the correct data
  const actualSelectedValues = useMemo(() => {
    if (!targetChecklistName) return [];
    
    const targetChecklist = !context.isSubflow
      ? context.checklist?.[targetChecklistName]
      : context.snapshot?.context.checklist?.[targetChecklistName];
      
    return targetChecklist
      ?.filter((item: { item: string; selected: boolean }) => item.selected)
      ?.map((item: { item: string }) => item.item) ?? [];
  }, [targetChecklistName, context.isSubflow, context.checklist, context.snapshot?.context.checklist]);

  // Update multiSelectedValues when checklist changes (e.g., when editing and data is preserved)
  useEffect(() => {
    debugUISync('--- REACT: Sync effect triggered ---', 
      `isMultiSelect: ${context.isMultiSelect}, checkName: ${context.checkName}, targetChecklistName: ${targetChecklistName}, actualSelectedValues: ${JSON.stringify(actualSelectedValues)}, currentMultiSelectedValues: ${JSON.stringify(multiSelectedValues)}, hasChecklistData: ${checklist.length > 0}, checklistOptions: ${checklist.length}`);
      
    // Sync if we're in multiselect mode OR if we have checklist data and values differ
    const valuesDiffer = JSON.stringify(actualSelectedValues.sort()) !== JSON.stringify(multiSelectedValues.sort());
    
    // Special case: If state machine has empty data but React state has values, prioritize state machine (cleared state)
    const stateIsEmpty = actualSelectedValues.length === 0;
    const reactHasValues = multiSelectedValues.length > 0;
    const shouldForceClear = stateIsEmpty && reactHasValues && checklist.length > 0;
    
    if (shouldForceClear) {
      debugUISync('--- REACT: Force clearing UI to match empty state machine data ---', 
        `State machine empty but React has values - likely after condition change`);
      setMultiSelectedValues([]);
    } else if (context.isMultiSelect || (checklist.length > 0 && valuesDiffer)) {
      debugUISync('--- REACT: Syncing multiselect UI with state machine data ---', `valuesDiffer: ${valuesDiffer}`);
      setMultiSelectedValues(actualSelectedValues);
    }
  }, [context.isMultiSelect, context.checkName, targetChecklistName, actualSelectedValues, checklist.length, multiSelectedValues]);

  const handleUpdateQuestion = useCallback(
    (
      currentEvent: string,
      currentFlow: string,
      selectedValue: string | string[],
      isMulti: boolean
    ) => {
      if (!currentEvent) {
        return;
      }

      // Get the current checkName for multiselects
      const currentCheckName = !context.isSubflow
        ? context.checkName
        : context.snapshot?.context.checkName;

      setQuestions((prevQuestions) => [
        ...prevQuestions,
        {
          question: currentEvent,
          options: isMulti
            ? options
            : [...getOptions(currentEvent, currentFlow)],
          selectedValues: selectedValue,
          isMultiSelect: isMulti,
          flow: currentFlow,
          checklistName: isMulti ? currentCheckName : undefined,
        },
      ]);
    },
    [getOptions, options, context.isSubflow, context.checkName, context.snapshot?.context.checkName]
  );

  const handleMultiSelectChange = useCallback(
    (selectedValues: string[]) => {
      setMultiSelectedValues([...selectedValues]);
      const sendFunction = !context.isSubflow
        ? sendTo
        : context.subflowActor?.send;
      
      // Use the current checkName instead of hardcoded "factors"
      const currentCheckName = !context.isSubflow
        ? context.checkName
        : context.snapshot?.context.checkName;
        
      options.forEach((option) => {
        sendFunction({
          type: "setMulti",
          item: option.value,
          name: currentCheckName || "factors", // fallback to "factors" if checkName is undefined
          selected: selectedValues.includes(option.value),
        });
      });
    },
    [context.isSubflow, context.subflowActor?.send, context.checkName, context.snapshot?.context.checkName, sendTo, options]
  );

  const processQuestion = useCallback(
    (question: Question) => {
      const { isMultiSelect, selectedValues } = question;

      if (isMultiSelect) {
        handleMultiSelectChange(selectedValues as string[]);
        send({ type: "next" });
        return;
      }

      send({ type: selectedValues as string });
    },
    [handleMultiSelectChange, send]
  );

  const handleEditQuestion = useCallback(
    (
      newValue: string | string[],
      position: number,
      isTriggeredFromMultiSelect: boolean,
      isSubflow: boolean
    ) => {
      const currentQuestion = questions[position];
      
      // Identify downstream multiselects that should be cleared BEFORE reset
      const downstreamMultiselects = questions.slice(position + 1).filter(q => q.isMultiSelect);
      
      // Detect actual condition changes vs multiselect edits
      // Condition questions typically contain "Condition?" or "condition?" in their name
      const isConditionChange = currentQuestion?.question?.includes('Condition?') || 
        currentQuestion?.question?.includes('condition?') ||
        (position === 0 && !currentQuestion?.isMultiSelect);
      
      const multiselectsToClear = isConditionChange ?
        [...Object.keys(context.checklist || {})] :           // Clear all for condition changes
        [...downstreamMultiselects.map(q => q.checklistName).filter(Boolean)]; // Clear downstream for multiselect/other edits

      // Clear multiselects from state machine context BEFORE reset
      if (multiselectsToClear.length > 0) {
        
        // Send events to clear specific multiselects
        multiselectsToClear.forEach(checklistName => {
          if (checklistName) {
            send({ type: "clearMultiselect", name: checklistName });
          }
        });

        // Force clear React multiselect state to prevent stale UI data
        setMultiSelectedValues([]);
      }

      // Reset the state machine AFTER clearing multiselects
      send({ type: "resetContext" });

      onReset?.();

      // Run pre-fire events
      runPreFiringEvents();

      // Only preserve questions up to the edit position
      // Don't clear multiselects that come before the edited position
      const newQuestions = questions.slice(0, position);
      setQuestions(newQuestions);
      
      // If no multiselects were cleared, check if we need to clear current multiselect UI state
      // This handles cases where the current multiselect is no longer relevant after editing
      if (multiselectsToClear.length === 0) {
        const currentCheckName = !context.isSubflow
          ? context.checkName
          : context.snapshot?.context.checkName;
        
        // Check if any remaining questions are multiselects with the same checklistName as current
        const hasCurrentMultiselectInRemaining = newQuestions.some(q => 
          q.isMultiSelect && q.checklistName === currentCheckName
        );
        
        // Only clear current multiselect state if it's not preserved in remaining questions
        if (!hasCurrentMultiselectInRemaining) {
          setMultiSelectedValues([]);
        }
      }

      // Determine if there are no questions left after the update
      const noQuestionsLeft = newQuestions.length === 0;

      const updateAndSend = () => {
        if (!isTriggeredFromMultiSelect) {
          send({ type: newValue as string });
          handleUpdateQuestion(
            currentQuestion.question,
            currentQuestion.flow,
            newValue,
            isTriggeredFromMultiSelect
          );
        }
      };

      if (!noQuestionsLeft) {
        newQuestions.forEach((question) => processQuestion(question));
      }

      updateAndSend();
    },
    [
      handleUpdateQuestion,
      onReset,
      processQuestion,
      questions,
      runPreFiringEvents,
      send,
    ]
  );

  const handleRadioChange = useCallback(
    (e: string, index: number) => {
      handleEditQuestion(e, index, false, context.isSubflow);
      handleDelayScroll(currentQuestionRef);
    },
    [context.isSubflow, handleEditQuestion]
  );

  const handleDisableCheckBox = useCallback(
    (position: number, isDisabled: boolean) => {
      setIsMultiSelectDisabled((prevMap) => {
        const newMap = new Map(prevMap);
        newMap.set(position, isDisabled);
        return newMap;
      });
    },
    []
  );

  const handleEditMultiSelect = useCallback(
    (index: number) => {
      // Don't clear multiSelectedValues here - let the sync effect handle it
      // when the state machine switches to the correct multiselect
      handleDisableCheckBox(index, false);
      handleEditQuestion(multiSelectedValues, index, true, context.isSubflow);
      handleDelayScroll(currentQuestionRef);
    },
    [
      handleDisableCheckBox,
      handleEditQuestion,
      multiSelectedValues,
      context.isSubflow,
    ]
  );

  const handleSubmitMultiSelect = useCallback(() => {
    send({ type: "next" });
    const flow = !context.isSubflow ? snapshot : context.snapshot;
    const flowId = !context.isSubflow ? snapshot?.id : context.subflow?.id;
    handleUpdateQuestion(flow.value, flowId, multiSelectedValues, true);
    handleDisableCheckBox(questions.length, true);
    handleDelayScroll(currentQuestionRef);
  }, [
    send,
    context.isSubflow,
    context.snapshot,
    context.subflow?.id,
    snapshot,
    handleUpdateQuestion,
    multiSelectedValues,
    handleDisableCheckBox,
    questions.length,
  ]);

  const renderQuestion = useCallback(
    (question: Question, index: number) => {
      const flowId = question.flow;

      return (
        <Wrapper key={`${question.question}_${index}`}>
          {!question.isMultiSelect ? (
            <RadioGroup
              options={question.options}
              description={getDescription(question.question, flowId)}
              title={getTitle(question.question, flowId)}
              onChange={(e) => handleRadioChange(e, index)}
              selectedValue={question.selectedValues as string}
            />
          ) : (
            <CheckboxGroup
              options={question.options as any}
              title={getTitle(question.question, flowId)}
              description={getDescription(question.question, flowId)}
              onChange={handleMultiSelectChange}
              onSubmit={() => handleEditMultiSelect(index)}
              disabled={isMultiSelectDisabled?.get(index)}
              selectedValues={question.selectedValues as string[]}
              submitButtonLabel={isMultiSelectDisabled ? "Edit" : "Next"}
            />
          )}
        </Wrapper>
      );
    },
    [
      getTitle,
      getDescription,
      handleRadioChange,
      handleEditMultiSelect,
      handleMultiSelectChange,
      isMultiSelectDisabled,
      multiSelectedValues,
    ]
  );

  const renderCurrentQuestion = useCallback(() => {
    const flow = !context.isSubflow ? snapshot : context.snapshot;
    const flowId = !context.isSubflow ? snapshot.id : context.subflow?.id;
    const radioOptions = getOptions(flow?.value, flowId);
    const title = getTitle(flow?.value, flowId);
    const description = getDescription(flow?.value, flowId);
    return !flow?.context.isMultiSelect ? (
      <RadioGroup
        options={radioOptions}
        title={title}
        description={description}
        onChange={(e) => {
          send({ type: e });
          handleUpdateQuestion(flow.value, flowId, e, false);
          handleDelayScroll(currentQuestionRef);
        }}
      />
    ) : (
      <CheckboxGroup
        options={options}
        title={title}
        description={description}
        onChange={handleMultiSelectChange}
        onSubmit={handleSubmitMultiSelect}
        submitButtonLabel="Next"
        selectedValues={multiSelectedValues}
      />
    );
  }, [
    context,
    snapshot,
    getOptions,
    getTitle,
    getDescription,
    options,
    handleMultiSelectChange,
    handleSubmitMultiSelect,
    send,
    handleUpdateQuestion,
  ]);

  return (
    <div>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      {questions.map(renderQuestion)}
      <div ref={currentQuestionRef}>
        {!isFormFinished && !errorMessage && renderCurrentQuestion()}
      </div>
    </div>
  );
};

export default StatelyForm;